import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  FaInfoCircle,
  FaSearch,
  FaPlus,
  FaSync,
  FaEllipsisV,
  FaRocket,
  FaTrashAlt,
  FaUndoAlt,
  FaChevronDown,
  FaChevronUp,
  FaPaperPlane,
  FaBullseye,
  FaEye,
  FaCog,
  FaSpinner,
} from "react-icons/fa";
import toast from "react-hot-toast";
import api from "@/api/config";
import ICONS from "@/assets/icons";
import Button from "../../inputs/Button";
import Input from "../../inputs/Input";
import { Tabs, TabsList, TabsTrigger } from "../../shedcnUI/tabs";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverItem,
} from "../../shedcnUI/popover";
import { Dialog, DialogContent, DialogTitle } from "../../shedcnUI/dialog";
import { cn } from "@/utils/methods";
import CampaignDetailsDrawer from "./components/CampaignDetailsDrawer";
import { CreateCampaignModal } from "./components/CreateCampaignModal";
import { EmailSendingStatusDrawer } from "./components/EmailSendingStatusDrawer";
import { CampaignStatusDrawer } from "@/pages/UseCampaignPage/components/CampaignStatusDrawer";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { Separator } from "@radix-ui/react-separator";
import PageHeader from "@/components/PageHeader";
import { useCampaignBackgroundTasks } from "./hooks/useCampaignBackgroundTasks";

interface Campaign {
  id: number;
  uuid: string;
  name: string;
  status: string;
  created_at: string;
  company_name: string;
  emails_count: number;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
  reply_count: number;
  open_rate: number;
  click_rate: number;
  processing_status?: string;
  task_id?: string;
}

type BackgroundTaskType = "generation" | "sending";

const CampaignStatusChip = ({ status }: { status: string }) => {
  const s = status.toLowerCase();
  let color = "bg-gray-100 text-gray-600";
  let Icon = ICONS.GreenCircleCheck;
  let useIcon = false;

  if (s === "completed" || s === "active" || s === "running") {
    color = "bg-green-light text-green-dark";
    Icon = ICONS.GreenCircleCheck;
    useIcon = true;
  } else if (s === "deleted") {
    color = "bg-red-light text-red-dark";
    Icon = ICONS.RedTriangle;
    useIcon = true;
  } else if (s === "draft") {
    Icon = ICONS.GreenCircleCheck;
    color = "bg-table-header-bg text-primary-black";
    useIcon = true;
  } else if (s === "processing" || s === "starting") {
    color = "bg-blue-50 text-blue-700";
    useIcon = false;
  }

  return (
    <span
      className={cn(
        "flex items-center space-x-1.5 px-[7px] py-[6px] w-fit rounded-[6px] text-[12px] font-semibold",
        color,
      )}
    >
      {useIcon && <Icon className="w-3 h-3" />}
      <span className="capitalize leading-3">{status.replace(/_/g, " ")}</span>
    </span>
  );
};

const MyCampaignsOnePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<
    "All" | "Processing" | "Active" | "Draft" | "Deleted"
  >("All");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUnsentExpanded, setIsUnsentExpanded] = useState(false);
  const [isProcessingExpanded, setIsProcessingExpanded] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(
    null,
  );

  const [isSendingDrawerOpen, setIsSendingDrawerOpen] = useState(false);
  const [sendingTaskId, setSendingTaskId] = useState<string>("");
  const [sendingCampaignName, setSendingCampaignName] = useState<string>("");

  // Generation progress drawer (reuses existing status drawer used on UseCampaignPage)
  const [isStatusDrawerOpen, setIsStatusDrawerOpen] = useState(false);
  const [statusCampaign, setStatusCampaign] = useState<Campaign | null>(null);

  const openStatusDrawerFor = (campaign: Campaign) => {
    setStatusCampaign(campaign);
    setIsStatusDrawerOpen(true);
  };

  const openSendingDrawer = (taskId: string, name?: string) => {
    if (!taskId) return;
    setSendingTaskId(taskId);
    setSendingCampaignName(name || "");
    setIsSendingDrawerOpen(true);
  };

  const deleteCampaignMutation = useMutation(
    (uuid: string) => api.post(`/campaign/${uuid}/delete`),
    {
      onSuccess: () => {
        toast.success("Campaign deleted");
        queryClient.invalidateQueries("campaigns");
      },
      onError: () => {
        toast.error("Failed to delete campaign");
      },
    },
  );

  const restoreCampaignMutation = useMutation(
    (uuid: string) => api.post(`/campaign/${uuid}/restore`),
    {
      onSuccess: () => {
        toast.success("Campaign restored");
        queryClient.invalidateQueries("campaigns");
      },
      onError: () => {
        toast.error("Failed to restore campaign");
      },
    },
  );

  const {
    data: campaignsData,
    isLoading,
    refetch,
  } = useQuery<{ campaigns: Campaign[] }>(
    "campaigns",
    async () => {
      const response = await api.get("/my-campaigns", {
        headers: { Accept: "application/json" },
      });
      return response.data;
    },
    {
      refetchInterval: (data) => {
        const hasProcessing = data?.campaigns?.some(
          (c) => c.status === "processing" || c.status === "starting",
        );
        return hasProcessing ? 5000 : false;
      },
    },
  );

  useEffect(() => {
    if (isLoading || isCreateModalOpen) return;

    const shouldOpenCreateModal =
      sessionStorage.getItem("openCreateCampaignModal") === "true";
    if (!shouldOpenCreateModal) return;

    sessionStorage.removeItem("openCreateCampaignModal");
    const timeoutId = window.setTimeout(() => {
      setIsCreateModalOpen(true);
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [isLoading, isCreateModalOpen]);

  const campaigns = React.useMemo(
    () => campaignsData?.campaigns || [],
    [campaignsData?.campaigns],
  );

  const { sendingCampaigns: sendingTaskSnapshots } = useCampaignBackgroundTasks(
    campaigns,
    { pollMs: 7000 },
  );

  const searchedCampaigns = campaigns.filter(
    (c) =>
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredCampaigns = searchedCampaigns.filter((c) => {
    if (activeTab === "All") return true;
    if (activeTab === "Processing")
      return c.status === "processing" || c.status === "starting";
    if (activeTab === "Active")
      return c.status === "active" || c.status === "running";
    if (activeTab === "Draft") return c.status === "draft";
    if (activeTab === "Deleted") return c.status === "deleted";
    return true;
  });

  const stats = {
    all: campaigns.length,
    processing: campaigns.filter(
      (c) => c.status === "processing" || c.status === "starting",
    ).length,
    active: campaigns.filter(
      (c) => c.status === "active" || c.status === "running",
    ).length,
    draft: campaigns.filter((c) => c.status === "draft").length,
    deleted: campaigns.filter((c) => c.status === "deleted").length,
  };

  const completedUnsentCampaigns = campaigns.filter(
    (c) =>
      c.status !== "deleted" &&
      (c.processing_status === "completed" || c.status === "active") &&
      c.emails_count > 0 &&
      c.sent_count < c.emails_count,
  );

  const totalUnsentEmails = completedUnsentCampaigns.reduce(
    (sum, c) => sum + (c.emails_count - c.sent_count),
    0,
  );

  const processingCampaigns = campaigns.filter(
    (c) =>
      c.status !== "deleted" &&
      (c.status === "processing" ||
        c.status === "starting" ||
        c.processing_status?.includes("processing")),
  );

  const sendingCampaigns = campaigns.filter((c) =>
    sendingTaskSnapshots.some((s) => s.campaignUuid === c.uuid),
  );

  return (
    <>
      <CampaignDetailsDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        campaign={selectedCampaign}
        onViewProcess={(c) => {
          const taskId =
            (c as any)?.latest_sending_task_id ||
            sendingTaskSnapshots.find((s) => s.campaignUuid === c?.uuid)
              ?.taskId;

          if (taskId) {
            openSendingDrawer(taskId, c?.name);
          } else {
            toast.error("No active sending process found for this campaign");
          }
        }}
      />

      <CampaignStatusDrawer
        open={isStatusDrawerOpen}
        onOpenChange={setIsStatusDrawerOpen}
        campaignName={statusCampaign?.name || ""}
        campaignUuid={statusCampaign?.uuid}
        taskId={statusCampaign?.task_id}
      />

      <EmailSendingStatusDrawer
        open={isSendingDrawerOpen}
        onOpenChange={setIsSendingDrawerOpen}
        taskId={sendingTaskId}
        campaignName={sendingCampaignName}
      />

      <PageHeader title="My Campaigns" />

      <div className="max-w-[1360px] mx-auto px-4 sm:px-10 lg:px-20 flex flex-col pt-12 pb-3 w-full">
        <div className="flex flex-col items-start mb-6 space-y-4 text-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <h1 className="font-sans font-semibold text-xl leading-[1.3] tracking-normal capitalize">
              My Campaigns
            </h1>
            <div className="relative group">
              <span className="cursor-pointer text-gray-400 hover:text-gray-600 transition-colors">
                <IoIosInformationCircleOutline size={20} />
              </span>
              <p className="hidden group-hover:block absolute left-6 top-0 bg-black rounded-md text-white px-1.5 py-[3px] whitespace-wrap text-xs z-10 w-72 ">
                Manage and monitor your email marketing campaigns
              </p>
            </div>
          </div>

          <div className="flex flex-row w-full justify-between items-center">
            <div className="relative flex items-center w-[635px] h-[42px] rounded-[12px] border-[1px] border-[#7d7b78] pt-[11px] pr-[10px] pb-[11px] pl-[10px] gap-[4px]">
              <span className="text-gray-400">
                <FaSearch size={14} />
              </span>
              <Input
                type="text"
                placeholder="Search campaigns by name or company"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full font-sans font-normal text-[14px] focus:outline-none leading-heading-sm pl-2"
              />
            </div>

            <div className="flex w-auto bg-white">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
                className="w-[234px] bg-brand-blue text-white border-[1.4px] border-border-color h-[42px] rounded-[8px] opacity-100 px-4 py-2 gap-[4px] text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
              >
                <FaPlus size={12} />
                <span>Create New Campaign</span>
              </button>
            </div>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as any)}
          className="w-full !justify-start"
        >
          <TabsList className="flex space-x-8 mb-6 border-b border-gray-200 bg-transparent h-auto p-0 rounded-none w-full justify-start">
            {[
              { value: "All", count: stats.all },
              { value: "Active", count: stats.active },
              { value: "Draft", count: stats.draft },
              { value: "Deleted", count: stats.deleted },
            ].map(({ value, count }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="pb-3 text-[16px] font-semibold flex items-center data-[state=active]:border-b-[3px] data-[state=active]:border-[#006EFF] data-[state=active]:text-primary-black text-text-primary hover:text-text-primary px-1 data-[state=active]:shadow-none bg-transparent rounded-none p-0 h-auto gap-2"
              >
                <span>{value}</span>
                <span
                  className={cn(
                    "px-[5px] py-[1px] rounded-[7px] text-[14px] font-medium",
                    activeTab === value
                      ? "bg-gray-100 text-brand-blue"
                      : "bg-gray-100 text-gray-600",
                  )}
                >
                  {count}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Processing Campaigns Alert */}
          {processingCampaigns.length > 0 && (
            <div className="mb-6">
              <div
                onClick={() => setIsProcessingExpanded(!isProcessingExpanded)}
                className="rounded-[14px] bg-[#FFFBEB] border border-[#FEF3C7] px-5 py-3 flex-col items-center justify-between cursor-pointer hover:bg-[#FEF3C7] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FaCog
                      className="text-[#F59E0B] animate-spin-slow"
                      size={20}
                    />
                    <div>
                      <div className="flex items-center gap-3 mb-0.5">
                        <span className="font-medium text-[#92400E] text-base">
                          Processing Campaigns!
                        </span>
                        <span className="bg-[#FEF3C7] text-[#92400E] text-xs font-semibold px-2.5 py-1 rounded-full">
                          {processingCampaigns.length} active
                        </span>
                      </div>
                      <p className="text-sm text-[#B45309]">
                        {processingCampaigns.length} campaign
                        {processingCampaigns.length !== 1
                          ? "s are"
                          : " is"}{" "}
                        currently generating emails
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const campaign = processingCampaigns[0];
                        if (campaign) openStatusDrawerFor(campaign);
                      }}
                      className="bg-[#F59E0B] text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-[#D97706] transition-colors"
                    >
                      View Progress
                    </button>
                    <span className="text-[#B45309]">
                      {isProcessingExpanded ? (
                        <FaChevronUp size={14} />
                      ) : (
                        <FaChevronDown size={14} />
                      )}
                    </span>
                  </div>
                </div>
                {isProcessingExpanded && (
                  <>
                    <Separator className="my-4 h-[1px] bg-[#FEF3C7]" />
                    <div className="mt-2 p-4 border-dashed border border-[#FDE68A] rounded-[12px] bg-white/50 shadow-sm animate-in fade-in slide-in-from-top-2">
                      <h4 className="text-xs font-bold text-[#92400E] uppercase tracking-wider mb-3">
                        Generation in progress:
                      </h4>
                      <ul className="space-y-3">
                        {processingCampaigns.map((campaign) => {
                          return (
                            <li
                              key={campaign.uuid}
                              className="flex items-center justify-between group"
                            >
                              <div className="flex flex-col">
                                <span className="font-semibold text-[#92400E] text-sm">
                                  {campaign.name}
                                </span>
                                <span className="text-xs text-[#B45309]">
                                  {campaign.processing_status ||
                                    "Initializing emails..."}
                                </span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openStatusDrawerFor(campaign);
                                }}
                                className="opacity-0 group-hover:opacity-100 flex items-center gap-1.5 text-[#D97706] text-xs font-bold transition-opacity hover:underline"
                              >
                                <FaSpinner size={10} className="animate-spin" />{" "}
                                Check Status
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Email Sending Alert (background process) */}
          {sendingCampaigns.length > 0 && (
            <div className="mb-6">
              <div className="rounded-[14px] bg-[#EEF2FF] border border-[#C7D2FE] px-5 py-3 flex-col items-center justify-between hover:bg-[#E0E7FF] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FaPaperPlane className="text-[#4338CA]" size={18} />
                    <div>
                      <div className="flex items-center gap-3 mb-0.5">
                        <span className="font-medium text-[#312E81] text-base">
                          Sending Emails in Background
                        </span>
                        <span className="bg-[#C7D2FE] text-[#312E81] text-xs font-semibold px-2.5 py-1 rounded-full">
                          {sendingCampaigns.length} active
                        </span>
                      </div>
                      <p className="text-sm text-[#3730A3]">
                        {sendingCampaigns.length} campaign
                        {sendingCampaigns.length !== 1 ? "s are" : " is"}{" "}
                        currently sending emails
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        const c = sendingCampaigns[0];
                        const snap = c
                          ? sendingTaskSnapshots.find(
                              (s) => s.campaignUuid === c.uuid,
                            )
                          : undefined;
                        if (snap?.taskId)
                          openSendingDrawer(snap.taskId, c?.name);
                      }}
                      className="bg-[#4F46E5] text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-[#4338CA] transition-colors"
                    >
                      View Status
                    </button>
                  </div>
                </div>

                <Separator className="my-4 h-[1px] bg-[#C7D2FE]" />
                <div className="mt-2 p-4 border-dashed border border-[#A5B4FC] rounded-[12px] bg-white/60 shadow-sm">
                  <h4 className="text-xs font-bold text-[#312E81] uppercase tracking-wider mb-3">
                    Sending in progress:
                  </h4>
                  <ul className="space-y-3">
                    {sendingCampaigns.map((c) => {
                      const snap = sendingTaskSnapshots.find(
                        (s) => s.campaignUuid === c.uuid,
                      );
                      return (
                        <li
                          key={c.uuid}
                          className="flex items-center justify-between group"
                        >
                          <div className="flex flex-col">
                            <span className="font-semibold text-[#312E81] text-sm">
                              {c.name}
                            </span>
                            <span className="text-xs text-[#3730A3]">
                              Task: {snap?.taskId}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              snap?.taskId &&
                              openSendingDrawer(snap.taskId, c?.name)
                            }
                            className="opacity-0 group-hover:opacity-100 flex items-center gap-1.5 text-[#4338CA] text-xs font-bold transition-opacity hover:underline"
                          >
                            <FaSpinner size={10} className="animate-spin" />{" "}
                            Check Status
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Unsent Campaigns Alert */}
          {completedUnsentCampaigns.length > 0 && (
            <div className="mb-6">
              <div
                onClick={() => setIsUnsentExpanded(!isUnsentExpanded)}
                className="rounded-[14px] bg-light-blue px-5 py-3 flex-col items-center justify-between cursor-pointer hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center justify-between  ">
                  <div className="flex items-center gap-3">
                    <FaRocket className="text-[#F59E0B] -rotate-45" size={20} />
                    <div>
                      <div className="flex items-center gap-3 mb-0.5">
                        <span className="font-medium text-primary-black text-base">
                          Ready to Send!
                        </span>
                        <span className="bg-[#FEC2FE] text-primary-black text-xs font-semibold px-2.5 py-1 rounded-full">
                          {totalUnsentEmails} unsent emails
                        </span>
                      </div>
                      <p className="text-sm text-text-primary">
                        {completedUnsentCampaigns.length} campaign
                        {completedUnsentCampaigns.length !== 1
                          ? "s have"
                          : " has"}{" "}
                        generated emails ready to send
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const campaign = completedUnsentCampaigns[0];
                        if (campaign)
                          navigate(`/campaign/${campaign.uuid}/emails`);
                      }}
                      className="bg-brand-blue text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-blue-600 transition-colors"
                    >
                      Send Emails
                    </button> */}
                    <span className="text-text-primary">
                      {isUnsentExpanded ? (
                        <FaChevronUp size={14} />
                      ) : (
                        <FaChevronDown size={14} />
                      )}
                    </span>
                  </div>
                </div>
                <div></div>
                {isUnsentExpanded && (
                  <>
                    <Separator className="my-4 h-[1px] bg-blue-200" />
                    <div className="mt-2 p-4 border-dashed border border-border-color rounded-[12px] shadow-sm animate-in fade-in slide-in-from-top-2">
                      <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider mb-3">
                        Campaigns with unsent emails:
                      </h4>
                      <ul className="space-y-3">
                        {completedUnsentCampaigns.map((campaign) => {
                          const unsentCount =
                            campaign.emails_count - campaign.sent_count;
                          if (unsentCount <= 0) return null;
                          return (
                            <li
                              key={campaign.uuid}
                              className="flex items-center justify-between group"
                            >
                              <div className="flex flex-col">
                                <span className="font-semibold text-primary-black text-sm">
                                  {campaign.name}
                                </span>
                                <span className="text-xs text-text-primary">
                                  {unsentCount} unsent email
                                  {unsentCount > 1 ? "s" : ""} remaining
                                </span>
                              </div>
                              <button
                                onClick={() => navigate(`/campaign/${campaign.uuid}/emails`)}
                                className=" flex items-center gap-1.5 text-brand-blue text-xs font-bold transition-opacity hover:underline"
                              >
                                <FaPaperPlane size={10} /> View Emails
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl overflow-hidden flex-1">
            <div className="overflow-x-auto overflow-y-auto h-[460px] w-full custom-scrollbar">
              <table className="w-full text-left whitespace-nowrap min-w-full relative border-separate border-spacing-0">
                <thead className="bg-table-header-bg sticky top-0 z-10">
                  <tr className="bg-table-header-bg">
                    <th className="px-6 py-5 text-[14px] text-text-primary font-sans font-medium leading-[1.25] tracking-normal rounded-tl-[15px] rounded-bl-[15px]">
                      Campaign Name
                    </th>
                    {["Emails", "Status", "Created On"].map((text) => (
                      <th
                        key={text}
                        className="px-6 py-5 text-text-primary font-sans font-medium text-sm leading-5 tracking-normal"
                      >
                        {text}
                      </th>
                    ))}
                    <th className="px-6 py-5 text-[14px] text-text-primary font-sans font-medium leading-[1.25] rounded-tr-[15px] rounded-br-[15px]"></th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  <tr>
                    <td colSpan={5} className="h-[6px]"></td>
                  </tr>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex justify-center flex-col items-center">
                          <span className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mb-3"></span>
                          <span>Loading campaigns...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredCampaigns.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                          <div className="w-16 h-16 bg-blue-50 text-brand-blue rounded-full flex items-center justify-center mb-4 shadow-sm">
                            <FaRocket size={32} />
                          </div>
                          <h3 className="text-xl font-bold text-primary-black mb-2">
                            No campaigns yet
                          </h3>
                          <p className="text-sm text-text-primary mx-auto leading-relaxed">
                            Create your first AI-powered email campaign in
                            minutes.
                          </p>
                          <p className="text-sm text-text-primary mx-auto mb-8 leading-relaxed">
                            The AI researches each prospect and writes
                            personalized emails automatically.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-left">
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                              <div className="w-8 h-8 bg-orange-50 text-orange-500 rounded-lg flex items-center justify-center mb-3">
                                <FaSearch size={16} />
                              </div>
                              <div className="font-bold text-primary-black text-sm mb-1">
                                AI Research
                              </div>
                              <div className="text-xs text-text-primary">
                                Researches each prospect's company
                              </div>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                              <div className="w-8 h-8 bg-pink-50 text-pink-500 rounded-lg flex items-center justify-center mb-3">
                                <FaBullseye size={16} />
                              </div>
                              <div className="font-bold text-primary-black text-sm mb-1">
                                Personalized
                              </div>
                              <div className="text-xs text-text-primary">
                                Unique email per lead
                              </div>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                              <div className="w-8 h-8 bg-green-50 text-green-500 rounded-lg flex items-center justify-center mb-3">
                                <FaPaperPlane size={16} />
                              </div>
                              <div className="font-bold text-primary-black text-sm mb-1">
                                Smart Sending
                              </div>
                              <div className="text-xs text-text-primary">
                                Automated delivery with tracking
                              </div>
                            </div>
                          </div>
                          {/* <button
                            type="button"
                            onClick={() => setIsCreateModalOpen(true)}
                            className="bg-brand-blue text-white px-6 py-2.5 rounded-full font-bold hover:bg-blue-600 transition-colors flex items-center gap-2 shadow-md"
                          >
                            <FaPlus size={14} /> Create Your First Campaign
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCampaigns.map((campaign) => (
                      <React.Fragment key={campaign.uuid}>
                        <tr
                          onClick={() => {
                            setSelectedCampaign(campaign);
                            setIsDrawerOpen(true);
                          }}
                          className="group hover:bg-row-hover-bg transition-colors cursor-pointer bg-white"
                        >
                          <td className="px-6 py-3 border-none rounded-l-[12px]">
                            <div className="flex flex-col">
                              <span className="text-primary-black font-sans font-medium text-base leading-6 tracking-normal">
                                {campaign.name}
                              </span>
                              <span className="font-sans text-text-primary font-medium text-sm leading-5 tracking-normal mt-1">
                                {campaign.company_name || "No company"}
                              </span>
                            </div>
                          </td>

                          <td className="px-6 py-3 border-none">
                            <div className="flex items-center space-x-1.5">
                              <ICONS.EmailAt className="w-4 h-4 text-text-primary" />
                              <span className="font-sans font-medium text-base leading-6 tracking-normal text-primary-black">
                                <span className="text-green-600">
                                  {campaign.sent_count}
                                </span>
                                /{campaign.emails_count}
                              </span>
                            </div>
                          </td>

                          <td className="px-6 py-3 border-none">
                            <CampaignStatusChip status={campaign.status} />
                          </td>

                          <td className="px-6 py-3 border-none">
                            <span className="font-sans font-medium text-base leading-6 tracking-normal text-primary-black">
                              {campaign.created_at
                                ? format(
                                    new Date(campaign.created_at),
                                    "d MMMM, yyyy",
                                  )
                                : "-"}
                            </span>
                          </td>

                          <td className="px-6 py-3 border-none rounded-r-[12px]">
                            <div className="flex items-center justify-end">
                              <div
                                onClick={(e) => e.stopPropagation()}
                                className="w-auto h-auto rounded-[8px] pt-[4px] pr-[10px] pb-[4px] pl-[10px] gap-[10px] border border-[#E3E2E7] opacity-100 flex items-center"
                              >
                                <button
                                  type="button"
                                  title={
                                    campaign.status === "deleted"
                                      ? "Restore campaign"
                                      : "Refresh"
                                  }
                                  disabled={restoreCampaignMutation.isLoading}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (campaign.status === "deleted") {
                                      restoreCampaignMutation.mutate(
                                        campaign.uuid,
                                      );
                                    } else {
                                      refetch();
                                    }
                                    toast.success("Refreshed campaign data");
                                  }}
                                  className="py-1.5 pl-1 pr-2 border-r border-border-color text-dark-text hover:text-primary-black transition-colors disabled:opacity-50"
                                >
                                  <FaSync
                                    size={11}
                                    className={
                                      restoreCampaignMutation.isLoading &&
                                      campaign.status === "deleted"
                                        ? "animate-spin"
                                        : ""
                                    }
                                  />
                                </button>
                                <button
                                  type="button"
                                  title={"view campaign emails"}
                                  disabled={restoreCampaignMutation.isLoading}
                                  onClick={() =>
                                    navigate(
                                      `/campaign/${campaign.uuid}/emails`,
                                    )
                                  }
                                  className="py-1.5 pl-1 pr-2 border-r border-border-color text-dark-text hover:text-primary-black transition-colors disabled:opacity-50"
                                >
                                  <FaEye size={11} />
                                </button>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(e) => e.stopPropagation()}
                                      className="py-1.5 pl-2 pr-1 text-dark-text hover:text-primary-black"
                                      aria-label="Campaign actions"
                                    >
                                      <FaEllipsisV size={11} />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent align="end" sideOffset={8}>
                                    {campaign.status === "deleted" ? (
                                      <PopoverItem
                                        icon={<FaUndoAlt size={12} />}
                                        disabled={
                                          restoreCampaignMutation.isLoading
                                        }
                                        onClick={() =>
                                          restoreCampaignMutation.mutate(
                                            campaign.uuid,
                                          )
                                        }
                                      >
                                        Restore Campaign
                                      </PopoverItem>
                                    ) : (
                                      <>
                                        {/* <PopoverItem
                                          icon={<FaEye size={12} />}
                                          onClick={() => navigate(`/campaign/${campaign.uuid}/emails`)}
                                        >
                                          View Emails
                                        </PopoverItem> */}
                                        <PopoverItem
                                          icon={<FaRocket size={12} />}
                                          onClick={() => {
                                            navigate(
                                              `/campaign/${campaign.uuid}/use`,
                                            );
                                          }}
                                        >
                                          Use Campaign
                                        </PopoverItem>
                                        <PopoverItem
                                          icon={<FaTrashAlt size={12} />}
                                          variant="danger"
                                          disabled={
                                            deleteCampaignMutation.isLoading
                                          }
                                          onClick={() => {
                                            setCampaignToDelete(campaign);
                                            setIsDeleteModalOpen(true);
                                          }}
                                        >
                                          Delete
                                        </PopoverItem>
                                      </>
                                    )}
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={5}
                            className="h-[6px] border-b border-border-light"
                          ></td>
                        </tr>
                        <tr>
                          <td colSpan={5} className="h-[6px]"></td>
                        </tr>
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tabs>
      </div>

      {isCreateModalOpen && (
        <CreateCampaignModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}

      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent
          className="max-w-md p-0 overflow-hidden border-none shadow-2xl"
          onClose={setIsDeleteModalOpen}
        >
          <div className="p-0">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
                <FaTrashAlt className="text-red-500 text-base" />
              </div>
              <div className="mb-0!">
                <DialogTitle className="text-xl font-semibold text-[#17150b] leading-tight">
                  Delete Campaign?
                </DialogTitle>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[#7d7b78] leading-relaxed">
                You are about to delete{" "}
                <span className="font-bold text-[#17150b]">
                  {campaignToDelete?.name}
                </span>
                . This action will permanently remove all emails, settings, and
                analytics associated with this campaign.
              </p>

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3">
                <FaInfoCircle className="text-amber-500 mt-1 flex-shrink-0" />
                <p className="text-sm text-amber-800 leading-snug">
                  <span className="font-bold">Warning:</span> This action is
                  permanent and cannot be reversed.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                className="flex-1 px-4 py-3 bg-[#f8f8f8] hover:bg-gray-200 text-[#17150b] text-base font-semibold rounded-[10px] transition-all"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-3 bg-[#fef3f2]  border border-[#ef4444] whitespace-nowrap text-[#ef4444] text-sm font-semibold rounded-[10px] transition-all flex items-center justify-center gap-2"
                disabled={deleteCampaignMutation.isLoading}
                onClick={() => {
                  if (campaignToDelete) {
                    deleteCampaignMutation.mutate(campaignToDelete.uuid, {
                      onSuccess: () => {
                        setIsDeleteModalOpen(false);
                        setCampaignToDelete(null);
                      },
                    });
                  }
                }}
              >
                {deleteCampaignMutation.isLoading ? (
                  <FaSync className="animate-spin" />
                ) : (
                  <FaTrashAlt />
                )}
                Delete Permanently
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyCampaignsOnePage;
